/**
 * Service container — single typed object holding the chosen impls.
 *
 * Convention: never `new` a provider in app code. Always read from
 * `container.<slot>` so the test container can swap in fakes.
 */

import { storage } from './storage';
import { MockAuthProvider, DEFAULT_MOCK_FIXTURE } from './providers/mock/MockAuthProvider';
import { SupabaseAuthProvider } from './providers/supabase-auth/SupabaseAuthProvider';
import type { IStorage } from './interfaces/IStorage';
import type { IAuthProvider } from './interfaces/IAuthProvider';
import type { IListRepository } from './interfaces/IListRepository';
import type { IItemRepository } from './interfaces/IItemRepository';
import type { IPushProvider } from './interfaces/IPushProvider';

export interface ServiceContainer {
  storage: IStorage;
  auth?: IAuthProvider;
  lists?: IListRepository;
  items?: IItemRepository;
  push?: IPushProvider;
}

/**
 * VITE_AUTH_MODE selects the auth provider:
 *   - 'mock'     → MockAuthProvider (dev/test)
 *   - 'supabase' → SupabaseAuthProvider (production — email + OAuth)
 *
 * The mode is build-time so production bundles never include mock code.
 */
const AUTH_MODE = (import.meta.env.VITE_AUTH_MODE as string | undefined) ?? 'supabase';

function chooseAuth(): IAuthProvider {
  if (AUTH_MODE === 'mock') {
    return new MockAuthProvider({ storage, initialFixture: DEFAULT_MOCK_FIXTURE });
  }
  if (AUTH_MODE === 'supabase') {
    return new SupabaseAuthProvider({ storage });
  }
  throw new Error(
    `container: VITE_AUTH_MODE='${AUTH_MODE}' is not recognized. ` +
      `Valid values: 'supabase' (production), 'mock' (dev).`,
  );
}

export const container: ServiceContainer = {
  storage,
  auth: chooseAuth(),
};
